import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';

import useObserver from '../../hooks/useObserver';

import './ImageRevealWrapper.module.css';

const animationDelays = [
  { type: 'none', value: '0', multiplier: '0' },
  { type: 'fade', value: '.5', multiplier: '0.2' },
  { type: 'slide', value: '.75', multiplier: '0.3' },
  { type: 'appear', value: '.75', multiplier: '0.3' },
  { type: 'fadeout', value: '0', multiplier: '0' }
];

const ImageRevealWrapper = ({
  color = '255,255,255',
  beginningOpacity = 1,
  endingOpacity = 0,
  transition = 'none',
  delay = 0,
  children
}) => {
  const wrapperRef = useRef();
  const [inview, setInview] = useState(false);

  const start = Number(beginningOpacity);
  const end = Number(endingOpacity);

  const [opacity, setOpacity] = useState(start);
  const [minHeight, setMinHeight] = useState(0);

  const [animateChildren, setAnimateChildren] = useState(false);

  const memoizedCallback = useCallback(div => {
    setInview(div[0].isIntersecting);
  }, []);

  const [attach, detach] = useObserver(memoizedCallback, {
    rootMargin: '10px'
  });

  let animate;

  const {
    value: animationDelay,
    multiplier: siblingDelay
  } = animationDelays.find(delay => delay.type === transition);

  const updateOpacity = () => {
    animate = window.requestAnimationFrame(updateOpacity);
    const box = wrapperRef.current.getBoundingClientRect();
    const halfHeight = box.height / 2;
    if (box.top < 0 && Math.abs(box.top) < halfHeight) {
      const percent = (box.top + halfHeight) / halfHeight;
      setOpacity(end + percent * (start - end));
      return;
    }
    if (box.top > 0) {
      setOpacity(start);
      return;
    }
    return;
  };

  const updateContainerHeight = container => {
    const additionalMarginHeight = 40 * container.childElementCount;
    const totalHeight = container.scrollHeight + additionalMarginHeight;
    setMinHeight(totalHeight);
  };

  useEffect(() => {
    updateContainerHeight(wrapperRef.current);
  }, []);

  useEffect(
    () => {
      if (inview) {
        window.requestAnimationFrame(updateOpacity);
        setAnimateChildren(true);
      } else {
        window.cancelAnimationFrame(animate);
      }
      attach(wrapperRef.current.parentElement);
      return () => {
        if (wrapperRef.current) {
          detach(wrapperRef.current.parentElement);
        }
      };
    },
    [inview]
  );

  const content =
    transition && !(transition === 'none')
      ? React.Children.map(children, (Child, index) => (
          <span
            style={{
              animationDelay:
                transition === 'fadeout'
                  ? 0
                  : `${(index * 2 + animationDelay) * siblingDelay + delay}s`,
              opacity: transition === 'fadeout' ? opacity : null
            }}
            styleName={classNames(transition, { animate: animateChildren })}
          >
            {Child}
          </span>
        ))
      : children;

  return (
    <div
      styleName={transition === 'appear' ? 'container-appear' : 'container'}
      style={{
        backgroundColor: `rgba(${color},${opacity})`,
        minHeight:
          transition === 'fade' || transition === 'fadeout' ? '0' : minHeight
      }}
      ref={wrapperRef}
    >
      {content}
    </div>
  );
};

export default ImageRevealWrapper;
